import { z } from "zod";
import type { ComplaintCreatePayload } from "../modules/core/payloads/cmtsTypedPayloads.js";

const guid = z.string().uuid();
const dateTime = z.string().datetime({ offset: true });
const nullableGuid = guid.nullable().optional();
const nullableDate = dateTime.nullable().optional();
const nullableText = z.string().nullable().optional();

export const complaintSummarySchema = z.object({
  id: guid,
  complaintCode: z.string().min(1),
  title: z.string().min(1),
  status: z.string().min(1),
  severity: z.string().min(1),
  slaState: z.string().min(1),
  createdAt: dateTime,
}).strict();

export const complaintDetailSchema = z.object({
  id: guid,
  complaintCode: z.string().min(1),
  title: z.string().min(1),
  description: z.string(),
  sourceId: guid,
  sourceCode: z.string().min(1),
  sourceName: z.string().min(1),
  categoryId: guid,
  categoryCode: z.string().min(1),
  categoryName: z.string().min(1),
  severityId: guid,
  severityCode: z.string().min(1),
  severityName: z.string().min(1),
  priorityId: guid,
  priorityCode: z.string().min(1),
  priorityName: z.string().min(1),
  currentStatusId: guid,
  statusCode: z.string().min(1),
  statusName: z.string().min(1),
  slaStateId: guid,
  slaStateCode: z.string().min(1),
  slaStateName: z.string().min(1),
  organizationUnitId: guid,
  organizationUnitCode: z.string().min(1),
  organizationUnitName: z.string().min(1),
  ownerId: nullableGuid,
  ownerName: nullableText,
  expertId: nullableGuid,
  expertName: nullableText,
  createdAt: dateTime,
  updatedAt: nullableDate,
  responseDueAt: nullableDate,
  resolutionDueAt: nullableDate,
  isConfidential: z.boolean(),
  isRepeat: z.boolean(),
  reopenedCount: z.number().int().nonnegative(),
  escalationLevel: z.number().int().nonnegative(),
  rowVersion: nullableText,
  visibleSections: z.array(z.enum(["OVERVIEW", "RCA", "CAPA", "VERIFICATION", "EVIDENCE", "MESSAGES", "TASKS", "TIMELINE", "AUDIT"])),
}).strict();

export const complaintCreateResultSchema = z.object({
  complaintId: guid,
  complaintCode: z.string().min(1),
  currentStatusId: guid,
  ownerUserId: nullableGuid,
  expertUserId: nullableGuid,
  createdAt: dateTime,
}).strict();

export const complaintWorkflowMutationResultSchema = z.object({
  complaintId: guid,
  complaintCode: z.string().min(1),
  currentStatusId: guid,
  updatedAt: nullableDate,
  rowVersion: nullableText,
}).strict();

export const complaintCreateSchemaFieldSchema = z.object({
  field: z.string().min(1),
  label: z.string().min(1),
  required: z.boolean(),
  controlType: z.string().min(1),
  endpoint: z.string().min(1),
  submittedValue: z.string().min(1),
}).strict();

export const complaintCreateSchemaSchema = z.object({
  entity: z.literal("Complaint"),
  endpoint: z.string().min(1),
  method: z.literal("POST"),
  requiredFields: z.array(z.string().min(1)),
  fields: z.array(complaintCreateSchemaFieldSchema),
}).strict();

export const complaintAllowedActionSchema = z.object({
  actionCode: z.string().min(1),
  displayLabel: z.string().min(1),
  allowed: z.boolean(),
  requiredPermission: z.string(),
  reasonCode: nullableText,
  reasonMessage: nullableText,
  recoveryHint: nullableText,
  nextStatusCode: nullableText,
  nextStageCode: nullableText,
  sideEffects: z.array(z.string()),
}).strict();

const deniedAccessSchema = z.object({
  code: z.string().min(1),
  reason: z.string().min(1),
  permissionCode: nullableText,
  routePath: nullableText,
  actionCode: nullableText,
  recoveryHint: nullableText,
}).strict();

export const complaintAllowedActionsEnvelopeSchema = z.object({
  complaintId: guid,
  complaintCode: nullableText,
  currentStatusCode: z.string(),
  currentStageCode: z.string(),
  snapshotVersion: z.string(),
  actions: z.array(complaintAllowedActionSchema),
  blockers: z.array(deniedAccessSchema),
  recoveryHints: z.array(z.string()),
}).strict();

export const complaintTimelineItemSchema = z.object({
  entityId: guid,
  complaintId: guid,
  entityType: z.string().min(1),
  actionCode: z.string().min(1),
  fromStatusId: nullableGuid,
  toStatusId: guid,
  applied: z.boolean(),
  sideEffectsQueued: z.boolean(),
  correlationId: z.string().min(1),
  appliedAtUtc: dateTime,
}).strict();

export const complaintTimelineEnvelopeSchema = z.object({
  complaintId: guid,
  events: z.array(complaintTimelineItemSchema),
}).strict();

export const complaintCommentSchema = z.object({
  id: guid,
  complaintId: guid,
  commentTypeId: guid,
  visibilityId: guid,
  message: z.string(),
  createdByUserId: guid,
  createdAt: dateTime,
  isInternalVisible: z.boolean(),
  isExternalVisible: z.boolean(),
}).strict();

export const complaintTaskSchema = z.object({
  id: guid,
  taskCode: z.string().min(1),
  complaintId: nullableGuid,
  title: z.string().min(1),
  assignedToUserId: nullableGuid,
  assignedToRoleId: nullableGuid,
  statusId: guid,
  dueAt: nullableDate,
  completedAt: nullableDate,
}).strict();

export const complaintMessageSchema = z.object({
  id: guid,
  messageThreadId: guid,
  senderUserId: guid,
  messageBody: z.string(),
  messageTypeCode: z.string().min(1),
  isInternal: z.boolean(),
  isSystemGenerated: z.boolean(),
  createdAt: dateTime,
}).strict();

export const complaintEvidenceSchema = z.object({
  id: guid,
  complaintId: guid,
  evidenceTypeId: guid,
  originalFileName: z.string().min(1),
  mimeType: z.string().min(1),
  fileSize: z.number().nonnegative(),
  fileHash: z.string(),
  isInternal: z.boolean(),
  isExternalVisible: z.boolean(),
  uploadedAt: dateTime,
  uploadedByUserId: guid,
  description: nullableText,
}).strict();

export type ComplaintListQuery = Readonly<{
  search?: string | undefined;
  sourceId?: string | undefined;
  categoryId?: string | undefined;
  severityId?: string | undefined;
  statusId?: string | undefined;
  slaStateId?: string | undefined;
  organizationUnitId?: string | undefined;
  ownerId?: string | undefined;
  fromDate?: string | undefined;
  toDate?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}>;

export type ComplaintSummaryDto = z.infer<typeof complaintSummarySchema>;
export type ComplaintListItem = ComplaintSummaryDto;
export type ComplaintDetailDto = z.infer<typeof complaintDetailSchema>;
export type ComplaintDto = ComplaintDetailDto;
export type ComplaintCreateResultDto = z.infer<typeof complaintCreateResultSchema>;
export type ComplaintWorkflowMutationResultDto = z.infer<typeof complaintWorkflowMutationResultSchema>;
export type ComplaintCreateSchemaDto = z.infer<typeof complaintCreateSchemaSchema>;
export type ComplaintAllowedActionDto = z.infer<typeof complaintAllowedActionSchema>;
export type ComplaintAllowedActionsEnvelopeDto = z.infer<typeof complaintAllowedActionsEnvelopeSchema>;
export type ComplaintTimelineItemDto = z.infer<typeof complaintTimelineItemSchema>;
export type ComplaintCommentDto = z.infer<typeof complaintCommentSchema>;
export type ComplaintTaskDto = z.infer<typeof complaintTaskSchema>;
export type ComplaintMessageDto = z.infer<typeof complaintMessageSchema>;
export type ComplaintEvidenceDto = z.infer<typeof complaintEvidenceSchema>;

export type ComplaintCreateRequest = ComplaintCreatePayload;
export type ComplaintUpdateRequest = Readonly<{
  reason?: string | undefined;
  comment?: string | undefined;
  evidenceIds?: readonly string[] | undefined;
  targetOwnerId?: string | undefined;
  targetExpertId?: string | undefined;
  expectedRowVersion?: string | undefined;
}>;

export type ComplaintWorkflowActionRequest = ComplaintUpdateRequest;

export function parseComplaintSummary(input: unknown): ComplaintSummaryDto { return complaintSummarySchema.parse(input); }
export function parseComplaintDetail(input: unknown): ComplaintDetailDto { return complaintDetailSchema.parse(input); }
export function parseComplaintCreateResult(input: unknown): ComplaintCreateResultDto { return complaintCreateResultSchema.parse(input); }
export function parseComplaintWorkflowResult(input: unknown): ComplaintWorkflowMutationResultDto { return complaintWorkflowMutationResultSchema.parse(input); }
export function parseComplaintCreateSchema(input: unknown): ComplaintCreateSchemaDto { return complaintCreateSchemaSchema.parse(input); }
export function parseComplaintAllowedActions(input: unknown): ComplaintAllowedActionsEnvelopeDto { return complaintAllowedActionsEnvelopeSchema.parse(input); }
export function parseComplaintTimeline(input: unknown): readonly ComplaintTimelineItemDto[] { return complaintTimelineEnvelopeSchema.parse(input).events; }
export function parseComplaintComments(input: unknown): readonly ComplaintCommentDto[] { return z.array(complaintCommentSchema).parse(input); }
export function parseComplaintTasks(input: unknown): readonly ComplaintTaskDto[] { return z.array(complaintTaskSchema).parse(input); }
export function parseComplaintMessages(input: unknown): readonly ComplaintMessageDto[] { return z.array(complaintMessageSchema).parse(input); }
export function parseComplaintEvidence(input: unknown): readonly ComplaintEvidenceDto[] { return z.array(complaintEvidenceSchema).parse(input); }
